// src/components/profesional/EditaImagesProfesionalForm.tsx

import { useEffect, useState } from 'react';
import ImageCarousel from '../componentesForm/carruselSlider/Carrusel'; // Carrusel de imágenes
import { ButtonSecundario } from '../../ui'; // Botón estilizado secundario
import ImagePopup from '../../popup/PopupImg'; // Componente modal para agregar imágenes
import { EditarDatosProfesionalProps } from '@/types'; // Tipado de props del componente

// Componente principal para editar imágenes de un profesional
const EditaImagesProfesional: React.FC<EditarDatosProfesionalProps> = ({
  images, // Imágenes ya guardadas del profesional
  onAddImage, // Función para agregar una imagen nueva
  onRemoveImage, // Función para eliminar una imagen existente
  perfil_profesional_id, // ID del profesional (debe ser number)
  pendingFiles, // Archivos que se han subido pero aún no se han guardado en el backend
}) => {
  // Estado para mostrar/ocultar el modal de agregar imagen
  const [showImageModal, setShowImageModal] = useState(false);

  // URLs de preview generadas a partir de `pendingFilesLocal`
  const [pendingURLs, setPendingURLs] = useState<string[]>([]);

  // Estado local para manejar archivos pendientes
  const [pendingFilesLocal, setPendingFilesLocal] = useState<File[]>(pendingFiles);

  // Máximo permitido
  const MAX_IMAGENES = 12;

  // Genera URLs de preview para los archivos pendientes
  useEffect(() => {
    const urls = pendingFilesLocal.map(f => URL.createObjectURL(f)); // Genera URLs temporales
    setPendingURLs(urls);

    // Limpieza: revocar URLs para liberar memoria cuando cambien los archivos
    return () => {
      urls.forEach(url => url && URL.revokeObjectURL(url));
    };
  }, [pendingFilesLocal]);

  // Mantiene sincronizado el estado local con los archivos pendientes que vienen de props
  useEffect(() => {
    setPendingFilesLocal(pendingFiles);
  }, [pendingFiles]);

  // Función para eliminar una imagen del carrusel
  const handleRemove = (index: number) => {
    if (index >= images.length) {
      // Si es un archivo pendiente y no guardado aún
      const pendIdx = index - images.length; // Calcula índice relativo
      const updated = [...pendingFilesLocal];
      updated.splice(pendIdx, 1); // Elimina el archivo pendiente
      setPendingFilesLocal(updated);

      // Revoca la URL temporal correspondiente
      if (pendingURLs[pendIdx]) {
        URL.revokeObjectURL(pendingURLs[pendIdx]);
      }
    } else {
      // Si es una imagen ya guardada, llama al callback de eliminación
      onRemoveImage?.(index);
    }
  };

  const handleAddImage = (file: File) => {
    const totalActual = images.length + pendingFilesLocal.length;

    if (totalActual >= MAX_IMAGENES) {
      alert(`Solo puedes subir un máximo de ${MAX_IMAGENES} imágenes.`);
      return;
    }

    onAddImage?.(file);
  };


  return (
    <div className="imagenesTrabajos space-y-2">
      {/* Botón para abrir el modal y agregar imágenes */}
      <ButtonSecundario
        onClick={() => {
          const totalActual = images.length + pendingFilesLocal.length;

          if (totalActual >= MAX_IMAGENES) {
            alert(`Solo puedes subir un máximo de ${MAX_IMAGENES} imágenes.`);
            return;
          }

          setShowImageModal(true);
        }}
      >
        Añadir Imagen
      </ButtonSecundario>


      {/* Carrusel de imágenes mostrando tanto las guardadas como las pendientes */}
      <ImageCarousel
        images={[
          ...images,
          ...pendingURLs.map(url => ({ imagen_trabajo_realizado: url })), // Convierte URLs a objeto esperado por el carrusel
        ]}
        onRemoveImage={handleRemove} // Callback para eliminar imágenes
        showNoImagesText={images.length + pendingFiles.length === 0} // Mensaje si no hay imágenes
      />

      {/* Modal para agregar nuevas imágenes */}
      {showImageModal && (
        <ImagePopup
          perfil_profesional_id={perfil_profesional_id}
          onAddImage={handleAddImage}
          onClose={() => setShowImageModal(false)}
        />

      )}
    </div>
  );
};

export default EditaImagesProfesional;
